// Invoice Chase — the Third Party tab's second view: vouchered, not billed.
//
// The vendor invoices AP has vouchered in JDE whose cost isn't on one of our
// client invoices (billing summaries) yet — one row per vendor invoice, oldest
// voucher first, so nothing we've been billed for sits unbilled to the
// client. A PO line counts once the live JDE match rollup (readPOMatchStatus →
// poStatus, the same source as the INV STATUS column) shows a voucher on it
// (Vouchered or Partially Vouchered) and its cost history rows (the OV
// receipts) still carry cost with no summary invoice number (t.inv). Lines
// held Not Billable never go on a client invoice, so they're left out.
//
// The vendor, description and cost type come off the open PO line when it is
// still open and off the job's cost history otherwise. The match rollup only
// carries a line's latest vendor invoice, so a line billed across two vendor
// invoices sits under the later one.
//
// Rows tick like the Lines grid: a ticked row selects its billable cost rows
// in billing.js's shared selection, so Create Invoice / Add to Invoice here
// open the same modal (and write the same rows) as they do from Lines.
// Nothing in this file writes on its own.

import React, { useMemo, useState, createElement } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import { pNum } from '../db/company';
import { jobOpenCommitmentsAtom } from '../store/company';
import { OutlineButton } from './procurementFilters';
import { tapProxyUrl } from './tapInvoiceViewer';

const card = {
    backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 16, paddingBottom: 16, paddingLeft: 18, paddingRight: 18, marginBottom: 16,
};
const th = { fontSize: 11, fontWeight: '700', color: '#475569', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };
const td = { fontSize: 12, color: '#1e293b', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };

const money = (n) => {
    const v = pNum(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + abs;
};
const ymd = (d) => (d ? moment.utc(d).format('YYYY-MM-DD') : '');
const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');
const daysBetween = (later, earlier) => (later && earlier ? moment.utc(later).diff(moment.utc(earlier), 'days') : null);
const uniq = (xs) => [...new Set(xs.filter(Boolean))];
const minOf = (xs) => xs.filter(Boolean).sort()[0] || '';
const maxOf = (xs) => xs.filter(Boolean).sort().pop() || '';
// Receipts and their reversals can net a line to pennies — that's not cost
// left to bill.
const hasCost = (n) => Math.abs(pNum(n)) >= 0.005;

export const lineKey = (po, line) => `${pNum(po)}|${pNum(line)}`;

// JDE match rollup statuses that mean the vendor's invoice is in.
export const VOUCHERED = ['Vouchered', 'Partially Vouchered'];

// A cost row that can still go on a client invoice: not billed, not held Not
// Billable, and priced — the Lines grid's selectedTxns rule.
export const invoiceable = (t) => !t.inv && !t.nb && !t.noRate;

const TONE = {
    'Not billed': { bg: '#fef3c7', fg: '#92400e' },
    'Partly billed': { bg: '#dbeafe', fg: '#1e40af' },
    'Not billable': { bg: '#f1f5f9', fg: '#475569' },
    Billed: { bg: '#dcfce7', fg: '#166534' },
    'No job cost': { bg: '#f8fafc', fg: '#64748b' },
};

// ── One row per PO line ──
// poStatus: { `${po}|${line}`: readPOMatchStatus row }; txns: billing.js's
// priced cost rows (carry po/pol, inv, nb); openCommitments:
// v_Form_Open_PO_Items rows; today: 'YYYY-MM-DD'; invAmtByKey: each billed
// cost row's frozen summary amount (falls back to the row's own bill).
export const chaseRows = ({ poStatus = {}, txns = [], openCommitments = [], today, invAmtByKey = {} }) => {
    const byLine = new Map();
    const get = (po, line) => {
        const k = lineKey(po, line);
        let r = byLine.get(k);
        if (!r) {
            // Shown as JDE keys it, minus the zero padding cost history carries.
            r = { key: k, po: String(po).trim().replace(/^0+(?=\d)/, ''), line: pNum(line), ms: null, oc: null, entries: [] };
            byLine.set(k, r);
        }
        return r;
    };
    Object.values(poStatus || {}).forEach((ms) => { if (ms && ms.PO_Number != null) get(ms.PO_Number, ms.PO_Line_Number).ms = ms; });
    (txns || []).forEach((t) => { if (t.po && t.pol !== '' && t.pol != null) get(t.po, t.pol).entries.push(t); });
    (openCommitments || []).forEach((o) => {
        if (o && o.PO_Number != null && o.PO_Line_Number != null) get(o.PO_Number, o.PO_Line_Number).oc = o;
    });

    return [...byLine.values()].map((r) => {
        const { ms, oc, entries } = r;
        const first = entries[0] || {};
        const recvDates = entries.map((t) => t.date).filter(Boolean).sort();
        const invDate = ymd(ms?.Last_Voucher_GL_Date);
        const desc = oc
            ? [oc.PO_Line_Description, oc.PO_Line_Description_2].map((s) => String(s || '').trim()).filter(Boolean).join(' ')
            : '';
        return {
            key: r.key,
            po: r.po,
            line: r.line,
            vendor: String(oc?.Supplier_AB_Name || first.who || '').trim(),
            desc: desc || first.ds || '',
            ct: oc?.Cost_Type_Code || first.ct || '',
            ctDesc: oc?.Cost_Type_Description || first.ctDesc || '',
            cc: oc?.Phase_Code || first.cc || '',
            firstRecv: recvDates[0] || ymd(ms?.Last_Received_Date),
            lastRecv: ymd(ms?.Last_Received_Date) || recvDates[recvDates.length - 1] || '',
            // The line has a JDE match row at all (a PO line, not bare job cost).
            matched: Boolean(ms),
            vouchered: VOUCHERED.includes(ms?.Match_Status),
            invNo: ms?.Latest_Supplier_Invoice_Number || '',
            invDate,
            pdf: ms?.TAP_URL || entries.find((t) => t.pdf)?.pdf || '',
            received: entries.reduce((s, t) => s + pNum(t.cost), 0) || pNum(oc?.PO_Received_Amount),
            // Cost still to go on a client invoice.
            unbilled: entries.filter((t) => !t.inv && !t.nb).reduce((s, t) => s + pNum(t.cost), 0),
            // Cost held Not Billable (by hand or the contract rules), and why.
            nbCost: entries.filter((t) => !t.inv && t.nb).reduce((s, t) => s + pNum(t.cost), 0),
            nbReasons: uniq(entries.filter((t) => !t.inv && t.nb).map((t) => t.nbReason)),
            // Our client invoices this line's cost already sits on, what they
            // bill for it, and the cost that covers — a part-billed line
            // stays listed for the rest.
            billedOn: uniq(entries.map((t) => t.inv)),
            invoiced: entries.filter((t) => t.inv).reduce((s, t) => s + pNum(invAmtByKey[t.key] ?? t.bill), 0),
            invoicedCost: entries.filter((t) => t.inv).reduce((s, t) => s + pNum(t.cost), 0),
            // Partly vouchered: received cost the vendor hasn't invoiced yet.
            notVouchered: ms?.Match_Status === 'Partially Vouchered' ? pNum(ms.Open_Receipt_Amount) : 0,
            days: daysBetween(today, invDate),
            // The line's cost rows themselves — what ticking its invoice bills.
            tx: entries,
        };
    });
};

// ── PO lines → one row per vendor invoice with cost left to bill ──
// Vouchered lines fold by vendor + invoice # (so two vendors' "1001" never
// merge); the row keeps only the lines with unbilled cost, but reads Partly
// billed when any of the invoice's cost is already on a client invoice.
// rest: fold every line instead — the "other vouchered" invoices with nothing
// left to bill, read as why (held Not Billable, billed, or no cost here).
const foldInvoice = (key, all, rest) => {
    const byPoLine = (a, b) => a.po.localeCompare(b.po) || a.line - b.line;
    const ls = (rest ? [...all] : all.filter((l) => hasCost(l.unbilled))).sort(byPoLine);
    if (!ls.length) return null;
    const nbCost = all.reduce((s, l) => s + l.nbCost, 0);
    const one = ls[0];
    const pos = uniq(ls.map((l) => l.po));
    const descs = uniq(ls.map((l) => l.desc));
    const cts = uniq(ls.map((l) => l.ct));
    const billedOn = uniq(all.flatMap((l) => l.billedOn));
    const days = ls.reduce((m, l) => (l.days != null && (m == null || l.days > m) ? l.days : m), null);
    return {
        key,
        invNo: one.invNo,
        invDate: minOf(all.map((l) => l.invDate)),
        vendor: one.vendor,
        po: one.po,
        line: one.line,
        pos,
        poText: pos.length > 1 ? `${pos[0]} +${pos.length - 1}` : pos[0] || '',
        lineText: pos.length > 1
            ? `${ls.length} lines`
            : `Line${ls.length === 1 ? '' : 's'} ${ls.map((l) => l.line).join(', ')}`,
        // Export form: 90032140-3, 90032140-4
        poLines: ls.map((l) => `${l.po}-${l.line}`).join(', '),
        desc: descs[0] || '',
        descs,
        ct: cts.join(', '),
        ctDesc: cts.length === 1 ? ls.find((l) => l.ct === cts[0]).ctDesc : '',
        cc: uniq(ls.map((l) => l.cc)).join(', '),
        firstRecv: minOf(ls.map((l) => l.firstRecv)),
        lastRecv: maxOf(ls.map((l) => l.lastRecv)),
        received: all.reduce((s, l) => s + l.received, 0),
        unbilled: ls.reduce((s, l) => s + l.unbilled, 0),
        notVouchered: ls.reduce((s, l) => s + l.notVouchered, 0),
        nbCost,
        nbReasons: uniq(all.flatMap((l) => l.nbReasons)),
        // Held Not Billable cost — what "Make billable" releases.
        nbTx: all.flatMap((l) => (l.tx || []).filter((t) => !t.inv && t.nb)),
        billedOn,
        // Across every line of the vendor invoice, billed ones included.
        invoiced: all.reduce((s, l) => s + (l.invoiced || 0), 0),
        invoicedCost: all.reduce((s, l) => s + (l.invoicedCost || 0), 0),
        status: rest
            ? (hasCost(nbCost) ? 'Not billable' : billedOn.length ? 'Billed' : 'No job cost')
            : billedOn.length ? 'Partly billed' : 'Not billed',
        pdf: (all.find((l) => l.pdf) || {}).pdf || '',
        days,
        lines: ls,
        tx: all.flatMap((l) => l.tx || []),
        // What ticking the row selects.
        open: ls.flatMap((l) => l.tx || []).filter(invoiceable),
        haystack: [...pos, ...ls.map((l) => l.desc), one.vendor, one.invNo, ...cts, ...ls.map((l) => l.ctDesc), ...billedOn].join(' ').toLowerCase(),
    };
};

const voucheredGroups = (lines) => {
    const byInv = new Map();
    lines.forEach((l) => {
        if (!l.vouchered) return;
        const k = l.invNo ? `i:${l.vendor.toUpperCase()}|${String(l.invNo).trim().toUpperCase()}` : 'l:' + l.key;
        if (!byInv.has(k)) byInv.set(k, []);
        byInv.get(k).push(l);
    });
    return [...byInv.entries()];
};

export const invoiceRows = (lines = []) => voucheredGroups(lines).map(([k, ls]) => foldInvoice(k, ls)).filter(Boolean);

// The vouchered invoices invoiceRows leaves out — nothing on them is left to
// bill — so the page can still account for every vendor invoice.
export const otherVoucheredRows = (lines = []) => voucheredGroups(lines)
    .filter(([, ls]) => !ls.some((l) => hasCost(l.unbilled)))
    .map(([k, ls]) => foldInvoice(k, ls, true))
    .filter(Boolean);

// Column widths — header and rows read from here.
const W = {
    sel: 34, inv: 118, vendor: 170, po: 100, desc: 230, ct: 120, recv: 88, recvAmt: 104, unbilled: 120, invoiced: 112, billed: 150, days: 84, pdf: 56,
};
const TABLE_W = Object.values(W).reduce((s, w) => s + w, 0);

const StatusPill = ({ status }) => {
    const c = TONE[status] || TONE['Not billed'];
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: c.bg, borderRadius: 999, paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 8, alignSelf: 'flex-start' }}>
            <View style={{ width: 5, height: 5, borderRadius: 999, backgroundColor: c.fg, marginRight: 5 }} />
            <Text style={{ fontSize: 10, fontWeight: '700', color: c.fg, whiteSpace: 'nowrap' }}>{status}</Text>
        </View>
    );
};

// Sitting 30+ days since the voucher reads amber, 60+ red.
const daysTone = (days) => {
    if (days == null) return '#94a3b8';
    if (days >= 60) return '#b91c1c';
    if (days >= 30) return '#b45309';
    return '#1e293b';
};

const Stat = ({ label, value, sub, tone, first }) => (
    <View style={{ marginRight: 18, marginBottom: 2, paddingLeft: first ? 0 : 18, borderLeftWidth: first ? 0 : 1, borderColor: '#e2e8f0' }}>
        <Text style={{ fontSize: 9, color: '#94a3b8', fontWeight: '700', letterSpacing: 0.6, marginBottom: 2, whiteSpace: 'nowrap' }}>{label}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            <Text style={{ fontSize: 15, fontWeight: '700', color: tone || '#0f172a', whiteSpace: 'nowrap' }}>{value}</Text>
            {sub ? <Text style={{ fontSize: 10, color: '#94a3b8', marginLeft: 5, whiteSpace: 'nowrap' }}>{sub}</Text> : null}
        </View>
    </View>
);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };
const byAge = (a, b) => (b.days ?? -1) - (a.days ?? -1) || a.vendor.localeCompare(b.vendor) || a.po.localeCompare(b.po) || a.line - b.line;

// Vendor groups, oldest voucher first.
const groupByVendor = (list) => {
    const m = new Map();
    list.forEach((r) => {
        const k = r.vendor || '(no vendor)';
        let g = m.get(k);
        if (!g) { g = { vendor: k, rows: [], lines: 0, unbilled: 0, invoiced: 0, nbCost: 0, received: 0, oldest: null }; m.set(k, g); }
        g.rows.push(r);
        g.lines += r.lines.length;
        g.unbilled += r.unbilled;
        g.invoiced += r.invoiced || 0;
        g.nbCost += r.nbCost;
        g.received += r.received;
        if (r.days != null && (g.oldest == null || r.days > g.oldest)) g.oldest = r.days;
    });
    return [...m.values()].sort((a, b) => (b.oldest ?? -1) - (a.oldest ?? -1) || b.unbilled - a.unbilled);
};

const BillingInvoiceChase = ({
    jobKey, txns, poStatus, openTapDoc, invAmtByKey,
    // From billing.js: the Lines grid's selection and its invoice actions.
    // Without setSel + CheckBox the list renders read-only.
    sel, setSel, selCount = 0, locked, canAdd, onCreate, onAdd, onOpenInvoice, onMakeBillable, CheckBox,
}) => {
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [search, setSearch] = useState('');
    const [byVendor, setByVendor] = useState(true);
    const [closed, setClosed] = useState({});
    const [restOpen, setRestOpen] = useState(false);

    const today = moment().format('YYYY-MM-DD');
    const lines = useMemo(
        () => chaseRows({ poStatus, txns, openCommitments: Array.isArray(openCommitments) ? openCommitments : [], today, invAmtByKey: invAmtByKey || {} }),
        [poStatus, txns, openCommitments, today, invAmtByKey],
    );
    const rows = useMemo(() => invoiceRows(lines), [lines]);
    // Invoices wholly on a client summary aren't listed — Invoices holds
    // them; restBilled keeps their count for the section's note.
    const { restRows, restBilled } = useMemo(() => {
        const all = otherVoucheredRows(lines);
        const rest = all.filter((r) => r.status !== 'Billed');
        return { restRows: rest, restBilled: all.length - rest.length };
    }, [lines]);

    const q = search.trim().toLowerCase();
    const visible = useMemo(() => rows.filter((r) => !q || r.haystack.includes(q)).sort(byAge), [rows, q]);
    const restVisible = useMemo(() => restRows.filter((r) => !q || r.haystack.includes(q)).sort(byAge), [restRows, q]);
    const groups = useMemo(() => (byVendor ? groupByVendor(visible) : null), [visible, byVendor]);
    const restGroups = useMemo(() => (byVendor ? groupByVendor(restVisible) : null), [restVisible, byVendor]);

    const unbilledAmt = rows.reduce((s, r) => s + r.unbilled, 0);
    const over30 = rows.filter((r) => r.days != null && r.days >= 30);
    const oldest = rows.reduce((m, r) => (r.days != null && r.days > m ? r.days : m), 0);
    const partly = rows.filter((r) => r.status === 'Partly billed');
    const nbRows = [...rows, ...restRows].filter((r) => hasCost(r.nbCost));
    const nbAmt = nbRows.reduce((s, r) => s + r.nbCost, 0);
    const restCount = (status) => restRows.filter((r) => r.status === status);

    // ── Selection ──
    // A row's box ticks its billable cost rows in billing.js's `sel`, the
    // same map the Lines grid ticks — so the modal sees one selection.
    const selectable = !!(setSel && CheckBox);
    const on = sel || {};
    const rowSome = (r) => r.open.some((t) => on[t.key]);
    const toggleRows = (list) => {
        const open = list.flatMap((r) => r.open);
        if (locked || !open.length) return;
        const pick = !open.every((t) => on[t.key]);
        setSel((prev) => {
            const next = { ...prev };
            open.forEach((t) => { if (pick) next[t.key] = true; else delete next[t.key]; });
            return next;
        });
    };
    const picked = rows.filter(rowSome);
    const pickedTx = rows.flatMap((r) => r.open.filter((t) => on[t.key]));
    const pickedCost = pickedTx.reduce((s, t) => s + pNum(t.cost), 0);
    const pickedBill = pickedTx.reduce((s, t) => s + pNum(t.bill), 0);
    // Lines ticked on the Lines view ride along into the same modal.
    const elsewhere = Math.max(0, selCount - pickedTx.length);

    const selCell = (list) => {
        const open = list.flatMap((r) => r.open);
        const all = open.length > 0 && open.every((t) => on[t.key]);
        const some = open.some((t) => on[t.key]);
        return (
            <Pressable
                onPress={(e) => { stop(e); toggleRows(list); }}
                style={{ width: W.sel, alignSelf: 'stretch', justifyContent: 'center', paddingLeft: 10, cursor: open.length && !locked ? 'pointer' : 'default' }}
            >
                <CheckBox
                    checked={all}
                    indeterminate={!all && some}
                    disabled={!open.length || locked}
                    onPress={(e) => { stop(e); toggleRows(list); }}
                />
            </Pressable>
        );
    };
    const blankSel = <View style={{ width: W.sel }} />;

    const exportRow = (r) => ({
        'Vendor Invoice #': r.invNo,
        'Voucher Date': fDate(r.invDate),
        Vendor: r.vendor,
        'PO Lines': r.poLines,
        Description: r.descs.join('; '),
        'Cost Type': r.ct,
        'Cost Type Description': r.ctDesc,
        'Cost Code': r.cc,
        'First Received': fDate(r.firstRecv),
        'Last Received': fDate(r.lastRecv),
        'Received $': Math.round(r.received * 100) / 100,
        'Not Billed $': Math.round(r.unbilled * 100) / 100,
        'Invoiced $': Math.round(r.invoiced * 100) / 100,
        'Invoiced Cost $': Math.round(r.invoicedCost * 100) / 100,
        'Not Vouchered $': Math.round(r.notVouchered * 100) / 100,
        'Not Billable $': Math.round(r.nbCost * 100) / 100,
        'Not Billable Reason': r.nbReasons.join('; '),
        'Client Invoice Status': r.status,
        'Already On Client Invoice': r.billedOn.join(', '),
        'Days Since Voucher': r.days ?? '',
        'TAP Invoice Link': r.pdf,
    });
    const exportExcel = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(visible.map(exportRow)), 'Vouchered Not Billed');
        if (restVisible.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(restVisible.map(exportRow)), 'Other Vouchered');
        XLSX.writeFile(wb, `Vouchered_Not_Billed_${jobKey}_${today}.xlsx`);
    };

    // rest: a row from the Other vouchered section — nothing on it to tick.
    const renderRow = (r, i, indent, rest) => {
        const canPick = selectable && !rest && r.open.length > 0 && !locked;
        const isPicked = selectable && !rest && rowSome(r);
        return (
            <Pressable
                key={r.key}
                onPress={canPick ? () => toggleRows([r]) : undefined}
                style={{
                    flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9',
                    backgroundColor: isPicked ? '#eff6ff' : i % 2 ? '#fafafa' : '#fff',
                    cursor: canPick ? 'pointer' : 'default',
                }}
            >
                {selectable && !rest ? selCell([r]) : blankSel}
                <View style={[td, { width: W.inv, paddingLeft: indent ? 22 : 8 }]}>
                    {r.invNo ? (
                        <>
                            <Text style={{ fontSize: 12, fontWeight: '600', color: '#0f172a' }} numberOfLines={1}>#{r.invNo}</Text>
                            {r.invDate ? <Text style={{ fontSize: 10, color: '#64748b' }}>{fDate(r.invDate)}</Text> : null}
                        </>
                    ) : <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>}
                </View>
                <Text style={[td, { width: W.vendor }]} numberOfLines={1}>{r.vendor || '—'}</Text>
                <View style={[td, { width: W.po }]}>
                    <Text style={{ fontSize: 12, color: '#1e293b' }} title={r.pos.length > 1 ? r.pos.join(', ') : undefined}>{r.poText}</Text>
                    <Text style={{ fontSize: 10, color: '#64748b' }} numberOfLines={1}>{r.lineText}</Text>
                </View>
                <View style={[td, { width: W.desc }]}>
                    <Text style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={r.descs.length > 1 ? 1 : 2}>{r.desc || '—'}</Text>
                    {r.descs.length > 1 ? (
                        <Text style={{ fontSize: 10, color: '#64748b' }} title={r.descs.join('\n')}>
                            +{r.descs.length - 1} more line{r.descs.length === 2 ? '' : 's'}
                        </Text>
                    ) : null}
                </View>
                <View style={[td, { width: W.ct }]}>
                    <Text style={{ fontSize: 12, color: '#1e293b' }} numberOfLines={1}>{r.ct || '—'}</Text>
                    {r.ctDesc ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{r.ctDesc}</Text> : null}
                </View>
                <View style={[td, { width: W.recv }]}>
                    <Text style={{ fontSize: 12, color: '#1e293b' }}>{fDate(r.firstRecv) || '—'}</Text>
                    {r.lastRecv && r.lastRecv !== r.firstRecv ? (
                        <Text style={{ fontSize: 10, color: '#94a3b8' }}>last {fDate(r.lastRecv)}</Text>
                    ) : null}
                </View>
                <Text style={[td, { width: W.recvAmt, textAlign: 'right', color: r.received ? '#1e293b' : '#94a3b8' }]}>
                    {r.received ? money(r.received) : '—'}
                </Text>
                <View style={[td, { width: W.unbilled, alignItems: 'flex-end' }]}>
                    {rest ? (
                        <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>
                    ) : (
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#92400e' }}>{money(r.unbilled)}</Text>
                    )}
                    {hasCost(r.notVouchered) ? (
                        <Text style={{ fontSize: 10, color: '#94a3b8' }} title="Received on a partly vouchered line — the vendor hasn't invoiced this part yet">
                            incl. {money(r.notVouchered)} not vouchered
                        </Text>
                    ) : null}
                    {hasCost(r.nbCost) ? (
                        <Text style={{ fontSize: 10, color: '#64748b' }} title={r.nbReasons.join('\n')}>
                            {rest ? '' : '+ '}{money(r.nbCost)} not billable
                        </Text>
                    ) : null}
                </View>
                {/* What our client invoices already bill for this vendor
                    invoice (frozen summary amounts), over the cost that covers. */}
                <View
                    title={r.billedOn.length ? `${money(r.invoiced)} billed on ${r.billedOn.join(', ')} for ${money(r.invoicedCost)} of cost` : undefined}
                    style={[td, { width: W.invoiced, alignItems: 'flex-end' }]}
                >
                    {r.billedOn.length ? (
                        <>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e40af' }}>{money(r.invoiced)}</Text>
                            <Text style={{ fontSize: 10, color: '#94a3b8' }}>{money(r.invoicedCost)} cost</Text>
                        </>
                    ) : <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>}
                </View>
                <View style={[td, { width: W.billed }]}>
                    <StatusPill status={r.status} />
                    {r.billedOn.length ? (
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 2 }}>
                            <Text style={{ fontSize: 10, color: '#64748b', marginRight: 3 }}>on</Text>
                            {r.billedOn.map((no, k) => (onOpenInvoice ? (
                                <Pressable key={no} onPress={(e) => { stop(e); onOpenInvoice(no); }} style={{ marginRight: 3, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 10, color: '#1e40af', fontWeight: '600' }}>{no}{k < r.billedOn.length - 1 ? ',' : ''}</Text>
                                </Pressable>
                            ) : (
                                <Text key={no} style={{ fontSize: 10, color: '#64748b', marginRight: 3 }}>{no}{k < r.billedOn.length - 1 ? ',' : ''}</Text>
                            )))}
                        </View>
                    ) : null}
                    {rest && r.status === 'Not billable' && r.nbReasons.length ? (
                        <Text style={{ fontSize: 10, color: '#64748b', marginTop: 2 }} numberOfLines={2} title={r.nbReasons.join('\n')}>
                            {r.nbReasons[0]}{r.nbReasons.length > 1 ? ` +${r.nbReasons.length - 1} more` : ''}
                        </Text>
                    ) : null}
                    {rest && r.status === 'No job cost' ? (
                        <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                            {r.tx.length ? 'receipts net to $0' : 'no cost rows on this job'}
                        </Text>
                    ) : null}
                    {/* Releases the held lines the way the Lines grid's
                        BILLABLE switch does — they then read Not billed and
                        move up into the tickable list. */}
                    {r.nbTx.length && onMakeBillable && !locked ? (
                        <Pressable
                            onPress={(e) => { stop(e); onMakeBillable(r.nbTx); }}
                            style={{ marginTop: 3, alignSelf: 'flex-start', cursor: 'pointer' }}
                        >
                            <Text
                                style={{ fontSize: 10, color: '#1e40af', fontWeight: '600', textDecorationLine: 'underline' }}
                                title={`Mark ${plural(r.nbTx.length, 'held line')} billable`}
                            >
                                Make billable
                            </Text>
                        </Pressable>
                    ) : null}
                </View>
                <View style={[td, { width: W.days, alignItems: 'flex-end' }]}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: rest ? '#64748b' : daysTone(r.days) }}>{r.days == null ? '—' : r.days}</Text>
                    {r.days != null ? <Text style={{ fontSize: 9, color: '#94a3b8' }}>since voucher</Text> : null}
                </View>
                <View style={[td, { width: W.pdf }]}>
                    {r.pdf ? createElement('a', {
                        href: tapProxyUrl(r.pdf) || r.pdf,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        onClick: (e) => {
                            // The row ticks on click — opening the PDF mustn't.
                            stop(e);
                            if (!openTapDoc) return;
                            e.preventDefault();
                            openTapDoc(r.pdf, [r.vendor, r.invNo && `#${r.invNo}`, `PO ${r.poLines}`].filter(Boolean).join(' · '));
                        },
                        style: { fontSize: 12, color: '#1e40af', fontWeight: 600, textDecoration: 'none' },
                    }, 'View') : <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>}
                </View>
            </Pressable>
        );
    };

    const header = (list) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }}>
            {list && selectable ? selCell(list) : blankSel}
            <Text style={[th, { width: W.inv }]}>VENDOR INV #</Text>
            <Text style={[th, { width: W.vendor }]}>VENDOR</Text>
            <Text style={[th, { width: W.po }]}>PO / LINES</Text>
            <Text style={[th, { width: W.desc }]}>DESCRIPTION</Text>
            <Text style={[th, { width: W.ct }]}>COST TYPE</Text>
            <Text style={[th, { width: W.recv }]}>RECEIVED</Text>
            <Text style={[th, { width: W.recvAmt, textAlign: 'right' }]}>RECEIVED $</Text>
            <Text style={[th, { width: W.unbilled, textAlign: 'right' }]}>NOT BILLED $</Text>
            <Text style={[th, { width: W.invoiced, textAlign: 'right' }]}>INVOICED $</Text>
            <Text style={[th, { width: W.billed }]}>CLIENT INVOICE</Text>
            <Text style={[th, { width: W.days, textAlign: 'right' }]}>DAYS</Text>
            <Text style={[th, { width: W.pdf }]}>INV PDF</Text>
        </View>
    );

    const renderGroups = (gs, rest) => gs.map((g) => {
        const ck = (rest ? 'r:' : 'v:') + g.vendor;
        const isClosed = !!closed[ck];
        const anyPicked = selectable && !rest && g.rows.some(rowSome);
        return (
            <View key={ck}>
                <View style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#e2e8f0', backgroundColor: anyPicked ? '#eff6ff' : '#f1f5f9' }}>
                    {selectable && !rest ? selCell(g.rows) : blankSel}
                    <Pressable
                        onPress={() => setClosed((p) => ({ ...p, [ck]: !p[ck] }))}
                        style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }}
                    >
                        <View style={{ width: W.inv + W.vendor + W.po + W.desc + W.ct + W.recv, flexDirection: 'row', alignItems: 'center', paddingTop: 7, paddingBottom: 7 }}>
                            <Text style={{ width: 14, fontSize: 11, color: '#475569' }}>{isClosed ? '▸' : '▾'}</Text>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }}>{g.vendor}</Text>
                            <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                                {plural(g.rows.length, 'invoice')} · {plural(g.lines, 'line')}
                            </Text>
                        </View>
                        <Text style={[td, { width: W.recvAmt, textAlign: 'right', fontWeight: '600' }]}>{g.received ? money(g.received) : '—'}</Text>
                        {rest ? (
                            <Text style={[td, { width: W.unbilled, textAlign: 'right', color: '#64748b' }]}>
                                {hasCost(g.nbCost) ? `${money(g.nbCost)} NB` : '—'}
                            </Text>
                        ) : (
                            <Text style={[td, { width: W.unbilled, textAlign: 'right', fontWeight: '700', color: '#92400e' }]}>{money(g.unbilled)}</Text>
                        )}
                        <Text style={[td, { width: W.invoiced, textAlign: 'right', fontWeight: '600', color: hasCost(g.invoiced) ? '#1e40af' : '#94a3b8' }]}>
                            {hasCost(g.invoiced) ? money(g.invoiced) : '—'}
                        </Text>
                        <View style={{ width: W.billed }} />
                        <View style={[td, { width: W.days, alignItems: 'flex-end' }]}>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: rest ? '#64748b' : daysTone(g.oldest) }}>
                                {g.oldest == null ? '—' : g.oldest}
                            </Text>
                            {g.oldest != null ? <Text style={{ fontSize: 9, color: '#94a3b8' }}>oldest</Text> : null}
                        </View>
                        <View style={{ width: W.pdf }} />
                    </Pressable>
                </View>
                {isClosed ? null : g.rows.map((r, i) => renderRow(r, i, true, rest))}
            </View>
        );
    });

    const loading = !Object.keys(poStatus || {}).length && !lines.length;
    const restParts = [
        restCount('Not billable').length ? `${restCount('Not billable').length} not billable (${money(restCount('Not billable').reduce((s, r) => s + r.nbCost, 0))})` : '',
        restBilled ? `${restBilled} already billed (not listed)` : '',
        restCount('No job cost').length ? `${restCount('No job cost').length} with no cost on this job` : '',
    ].filter(Boolean);

    return (
        <>
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 10 }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginRight: 10 }}>Vouchered, Not Billed</Text>
                    <Text style={{ fontSize: 11, color: '#64748b' }}>
                        Vendor invoices vouchered in JDE whose cost isn&apos;t on one of our client invoices yet, oldest voucher first. Tick invoices to bill them together.
                    </Text>
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 12 }}>
                    <Stat first label="NOT BILLED" value={money(unbilledAmt)} sub={`(${plural(rows.length, 'invoice')})`} tone="#92400e" />
                    <Stat label="WAITING 30+ DAYS" value={money(over30.reduce((s, r) => s + r.unbilled, 0))} sub={`(${over30.length})`} tone={over30.length ? '#b45309' : undefined} />
                    <Stat label="OLDEST VOUCHER" value={rows.length ? plural(oldest, 'day') : '—'} tone={oldest >= 60 ? '#b91c1c' : undefined} />
                    <Stat
                        label="PARTLY BILLED"
                        value={plural(partly.length, 'invoice')}
                        sub={partly.length ? `(${money(partly.reduce((s, r) => s + r.invoiced, 0))} invoiced)` : undefined}
                        tone={partly.length ? '#1e40af' : undefined}
                    />
                    <Stat label="NOT BILLABLE" value={money(nbAmt)} sub={`(${plural(nbRows.length, 'invoice')})`} tone={nbRows.length ? '#475569' : undefined} />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextInput
                        value={search}
                        onChangeText={setSearch}
                        placeholder="Search vendor, PO, description, invoice #..."
                        style={{
                            width: 300, fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                            paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                            marginRight: 12, marginBottom: 6,
                        }}
                    />
                    <Pressable onPress={() => setByVendor((v) => !v)} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 14, marginBottom: 6, cursor: 'pointer' }}>
                        <View style={{
                            width: 14, height: 14, borderRadius: 3, borderWidth: 1, borderStyle: 'solid',
                            borderColor: byVendor ? '#0f172a' : '#94a3b8', backgroundColor: byVendor ? '#0f172a' : '#fff',
                            alignItems: 'center', justifyContent: 'center',
                        }}>
                            {byVendor ? <Text style={{ fontSize: 10, color: '#fff', lineHeight: 12 }}>✓</Text> : null}
                        </View>
                        <Text style={{ fontSize: 12, color: '#1e293b', marginLeft: 6 }}>Group by vendor</Text>
                    </Pressable>
                    <OutlineButton label="Export to Excel" onPress={exportExcel} disabled={!visible.length && !restVisible.length} />
                    {selectable ? (
                        <>
                            <View style={{ width: 8 }} />
                            <Pressable
                                onPress={() => selCount && !locked && onCreate && onCreate()}
                                style={{
                                    paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14,
                                    borderRadius: 4, marginBottom: 6,
                                    backgroundColor: selCount && !locked ? '#0f172a' : '#cbd5e1',
                                }}
                            >
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>
                                    {selCount ? `Create Invoice (${selCount})` : 'Create Invoice'}
                                </Text>
                            </Pressable>
                            <View style={{ width: 8 }} />
                            {/* Only Draft / Kicked Back invoices take new lines. */}
                            <OutlineButton
                                label={selCount ? `Add to Invoice (${selCount})` : 'Add to Invoice'}
                                onPress={onAdd}
                                disabled={!selCount || locked || !canAdd}
                            />
                        </>
                    ) : null}
                </View>
                {selectable && (pickedTx.length || elsewhere) ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 4, paddingTop: 10 }}>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e40af', marginRight: 10, marginBottom: 6 }}>
                            {plural(picked.length, 'vendor invoice')} · {plural(pickedTx.length, 'cost line')}
                        </Text>
                        {pickedTx.length ? (
                            <Text style={{ fontSize: 12, color: '#475569', marginRight: 10, marginBottom: 6 }}>
                                {money(pickedCost)} cost → {money(pickedBill)} to bill
                            </Text>
                        ) : null}
                        {elsewhere ? (
                            <Text style={{ fontSize: 11, color: '#b45309', marginRight: 10, marginBottom: 6 }}>
                                + {plural(elsewhere, 'line')} ticked on the Lines view go on the invoice too
                            </Text>
                        ) : null}
                        <Pressable onPress={() => setSel({})} style={{ marginBottom: 6 }}>
                            <Text style={{ fontSize: 11, color: '#64748b', textDecorationLine: 'underline' }}>Deselect all</Text>
                        </Pressable>
                    </View>
                ) : null}
            </View>

            <View style={card}>
                <ScrollView horizontal>
                    <View style={{ width: TABLE_W }}>
                        {header(visible)}
                        {groups ? renderGroups(groups, false) : visible.map((r, i) => renderRow(r, i, false, false))}
                        {!visible.length ? (
                            <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 16, paddingBottom: 16, paddingLeft: 8 }}>
                                {loading
                                    ? 'Loading PO receipt and invoice status…'
                                    : search.trim() ? 'Nothing matches the search.'
                                    : restRows.length ? 'Nothing vouchered is left to bill — see Other vouchered invoices below.'
                                    : 'Every vouchered vendor invoice is on a client invoice.'}
                            </Text>
                        ) : null}
                    </View>
                </ScrollView>
            </View>

            {/* The other vouchered vendor invoices not yet billed — held Not
                Billable (by hand or a contract rule) or vouchered with no cost
                on this job — so the list above never hides one. Wholly billed
                ones only count in the note. */}
            {restRows.length ? (
                <View style={card}>
                    <Pressable onPress={() => setRestOpen((v) => !v)} style={{ flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', cursor: 'pointer' }}>
                        <Text style={{ width: 16, fontSize: 12, color: '#475569' }}>{restOpen ? '▾' : '▸'}</Text>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginRight: 10 }}>
                            Other vouchered invoices ({restRows.length})
                        </Text>
                        <Text style={{ fontSize: 11, color: '#64748b' }}>
                            Vendor invoices with nothing left to bill: {restParts.join(' · ')}.
                        </Text>
                    </Pressable>
                    {restOpen ? (
                        <ScrollView horizontal>
                            <View style={{ width: TABLE_W, marginTop: 12 }}>
                                {header(null)}
                                {restGroups ? renderGroups(restGroups, true) : restVisible.map((r, i) => renderRow(r, i, false, true))}
                                {!restVisible.length ? (
                                    <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 16, paddingBottom: 16, paddingLeft: 8 }}>Nothing matches the search.</Text>
                                ) : null}
                            </View>
                        </ScrollView>
                    ) : null}
                </View>
            ) : null}
        </>
    );
};

export default BillingInvoiceChase;
